import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, catchError, map, of } from "rxjs";
import { environment } from "@env";
import { KeycloakHelperService } from "./keycloak.helper.service";
import {
  ConfigurationPolicyCompleteDTO,
  UIConfigurationDTO
} from "../models/app-config.model";
import {
  DEFAULT_UI_CONFIGURATION,
  getDefaultConfigurationPolicyCompleteMock
} from "../mocks/default-ui-configuration.mock";

@Injectable({ providedIn: "root" })
export class AppConfigService {
  private readonly semPararUiConfigurationTitle = "Franquia Sem Parar";
  private readonly endpoint = `${environment.apiUrl}configuration-policy/client`;
  private readonly DRIVE_B_UI_CONFIGURATION_OPTION = "DRIVE_B";

  private defaultConfig: ConfigurationPolicyCompleteDTO =
    getDefaultConfigurationPolicyCompleteMock();

  private config$ = new BehaviorSubject<ConfigurationPolicyCompleteDTO>(
    this.defaultConfig
  );

  constructor(
    private http: HttpClient,
    private keycloakHelperService: KeycloakHelperService
  ) {}

  private base64ToObjectUrl(base64: string, type = "image/png"): string {
    if (!base64) return "";

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type });
    return URL.createObjectURL(blob);
  }

  findClientConfiguration() {
    if (
      !this.keycloakHelperService.isFleetAdminLogged() &&
      !this.keycloakHelperService.isDriverLogged() &&
      !this.keycloakHelperService.isBackofficeUserLogged()
    ) {
      this.applyConfig(this.defaultConfig);
      return of(this.defaultConfig);
    }

    return this.http.get<any>(this.endpoint).pipe(
      map((response) => {
        let configPolicy: ConfigurationPolicyCompleteDTO = response;

        if (
          response?.configurationPolicy?.uiConfigurationOption ===
          this.DRIVE_B_UI_CONFIGURATION_OPTION
        ) {
          configPolicy.uiConfiguration = { ...DEFAULT_UI_CONFIGURATION };
        } else if (response.uiConfiguration) {
          configPolicy.uiConfiguration.logoImage = this.base64ToObjectUrl(
            response.uiConfiguration.logoImage
          );

          configPolicy.uiConfiguration.loadingImage = this.base64ToObjectUrl(
            response.uiConfiguration.loadingImage
          );
        } else {
          configPolicy.uiConfiguration = { ...DEFAULT_UI_CONFIGURATION };
        }

        this.applyConfig(configPolicy);
        return configPolicy.uiConfiguration;
      }),
      catchError((err) => {
        this.applyConfig(this.defaultConfig);
        return of(this.defaultConfig);
      })
    );
  }

  findBudgetAiAgentEnabledByClientId(clientId: number) {
    if (!clientId) {
      return of(false);
    }

    return this.http.get<any>(`${this.endpoint}/${clientId}`).pipe(
      map(
        (response) =>
          response?.configurationPolicy?.budgetAiAgentEnabled === true
      ),
      catchError(() => of(false))
    );
  }

  private applyConfig(config: ConfigurationPolicyCompleteDTO) {
    this.config$.next(config);

    document.documentElement.style.setProperty(
      "--primary-color",
      this.getPrimaryColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--sidebar-background-color",
      this.getSidebarBackgroundColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--sidebar-text-color",
      this.getSidebarTextColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--sidebar-selected-background-color",
      this.getSidebarSelectedBackgroundColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--header-gradient-init-color",
      this.getHeaderGradientInitColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--header-gradient-final-color",
      this.getHeaderGradientFinalColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-primary-background-color",
      this.getButtonPrimaryBackgroundColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-primary-border-color",
      this.getButtonPrimaryBorderColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-primary-text-color",
      this.getButtonPrimaryTextColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-secondary-background-color",
      this.getButtonSecondaryBackgroundColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-secondary-border-color",
      this.getButtonSecondaryBorderColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-secondary-text-color",
      this.getButtonSecondaryTextColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-tertiary-background-color",
      this.getButtonTertiaryBackgroundColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-tertiary-border-color",
      this.getButtonTertiaryBorderColor(config.uiConfiguration)
    );
    document.documentElement.style.setProperty(
      "--button-tertiary-text-color",
      this.getButtonTertiaryTextColor(config.uiConfiguration)
    );

    this.updateFavicon(config.uiConfiguration?.loadingImage);

    document.title = config.uiConfiguration?.title || "DriveB Fleet";
  }

  private getPrimaryColor(uiConfiguration?: UIConfigurationDTO | null): string {
    return (
      uiConfiguration?.primaryButtonBackgroundColor ||
      uiConfiguration?.gradientFinalColor ||
      DEFAULT_UI_CONFIGURATION.primaryButtonBackgroundColor ||
      DEFAULT_UI_CONFIGURATION.gradientFinalColor ||
      "#007bc0"
    );
  }

  private getSidebarBackgroundColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.sidebarBackgroundColor ||
      DEFAULT_UI_CONFIGURATION.sidebarBackgroundColor ||
      "#36475d"
    );
  }

  private getSidebarTextColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.sidebarTextColor ||
      DEFAULT_UI_CONFIGURATION.sidebarTextColor ||
      "#ffffff"
    );
  }

  private getSidebarSelectedBackgroundColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.sidebarBackgroundSelectedColor ||
      DEFAULT_UI_CONFIGURATION.sidebarBackgroundSelectedColor ||
      "#005691"
    );
  }

  private getHeaderGradientInitColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.gradientInitColor ||
      DEFAULT_UI_CONFIGURATION.gradientInitColor ||
      "#ffffff"
    );
  }

  private getHeaderGradientFinalColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.gradientFinalColor ||
      DEFAULT_UI_CONFIGURATION.gradientFinalColor ||
      "#007bc0"
    );
  }

  private getButtonPrimaryBackgroundColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.primaryButtonBackgroundColor ||
      DEFAULT_UI_CONFIGURATION.primaryButtonBackgroundColor ||
      "#007bc0"
    );
  }

  private getButtonPrimaryBorderColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.primaryButtonBorderColor ||
      DEFAULT_UI_CONFIGURATION.primaryButtonBorderColor ||
      this.getButtonPrimaryBackgroundColor(uiConfiguration)
    );
  }

  private getButtonPrimaryTextColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.primaryButtonTextColor ||
      DEFAULT_UI_CONFIGURATION.primaryButtonTextColor ||
      "#ffffff"
    );
  }

  private getButtonSecondaryBackgroundColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.secondaryButtonBackgroundColor ||
      DEFAULT_UI_CONFIGURATION.secondaryButtonBackgroundColor ||
      "#ffffff"
    );
  }

  private getButtonSecondaryBorderColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.secondaryButtonBorderColor ||
      DEFAULT_UI_CONFIGURATION.secondaryButtonBorderColor ||
      "#007bc0"
    );
  }

  private getButtonSecondaryTextColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.secondaryButtonTextColor ||
      DEFAULT_UI_CONFIGURATION.secondaryButtonTextColor ||
      "#007bc0"
    );
  }

  private getButtonTertiaryBackgroundColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.tertiaryButtonBackgroundColor ||
      DEFAULT_UI_CONFIGURATION.tertiaryButtonBackgroundColor ||
      "#ffffff"
    );
  }

  private getButtonTertiaryBorderColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    if (uiConfiguration?.tertiaryButtonBorderColor === null) {
      return "transparent";
    }

    return (
      uiConfiguration?.tertiaryButtonBorderColor ||
      DEFAULT_UI_CONFIGURATION.tertiaryButtonBorderColor ||
      "transparent"
    );
  }

  private getButtonTertiaryTextColor(
    uiConfiguration?: UIConfigurationDTO | null
  ): string {
    return (
      uiConfiguration?.tertiaryButtonTextColor ||
      DEFAULT_UI_CONFIGURATION.tertiaryButtonTextColor ||
      "#a1191d"
    );
  }

  getConfig$() {
    return this.config$.asObservable();
  }

  get currentConfig() {
    return this.config$.value;
  }

  isSemPararUiConfiguration(): boolean {
    return (
      this.currentConfig?.uiConfiguration?.title ===
      this.semPararUiConfigurationTitle
    );
  }

  private updateFavicon(iconUrl: string) {
    let link: HTMLLinkElement | null =
      document.querySelector("link[rel~='icon']") ||
      document.createElement("link");

    link.type = "image/png";
    link.rel = "icon";
    link.href = iconUrl;

    if (!link.parentElement) {
      document.head.appendChild(link);
    }
  }
}
